import { AuthIntegration, UserData, ExportData } from '@privemind/auth';
import { PortfolioData } from '../types';

// Extend UserData for portfolio-specific data
export interface PortfolioUserData extends UserData {
  buckets: PortfolioData['buckets'];
  assets: PortfolioData['assets'];
  cacheData?: {
    'price-cache'?: any;
    'asset-details-cache'?: any;
    [key: string]: any;
  };
  lastModified?: string;
  version?: string;
}

/**
 * Integration adapter for portfolio data with the auth system
 */
export class PortfolioAuthIntegration implements AuthIntegration<PortfolioUserData> {
  private portfolioStore: any; // Will be set when portfolio store is available

  setPortfolioStore(portfolioStore: any): void {
    this.portfolioStore = portfolioStore;
  }

  async loadUserData(): Promise<PortfolioUserData> {
    // Return empty portfolio structure if no portfolio store
    if (!this.portfolioStore) {
      return { buckets: {}, assets: {} };
    }

    // Get current portfolio data from the store
    const safePortfolioData = this.portfolioStore.safePortfolioData || { buckets: {}, assets: {} };
    
    return {
      buckets: safePortfolioData.buckets,
      assets: safePortfolioData.assets,
      cacheData: safePortfolioData.cacheData || {},
      lastModified: new Date().toISOString(),
      version: '1.0.0'
    };
  }

  async saveUserData(data: PortfolioUserData, password: string, noRetry?: boolean): Promise<void> {
    console.log('Saving portfolio data:', data, 'with password length:', password.length, 'noRetry:', noRetry);
    
    // Update the portfolio store with the data
    if (this.portfolioStore && data) {
      this.portfolioStore.updatePortfolioDataFromAuth({
        buckets: data.buckets || {},
        assets: data.assets || {}
      });
    }
  }

  async getUserData(password: string): Promise<PortfolioUserData> {
    console.log('Getting user data with password length:', password.length);
    // Return current portfolio data - in a real app this would decrypt from auth system
    return this.loadUserData();
  }

  async exportUserData(): Promise<{ exportData: ExportData<PortfolioUserData>; filename: string }> {
    const userData = await this.loadUserData();
    
    return {
      exportData: {
        user: null as any, // Will be populated by auth system
        userData,
        exportedAt: new Date().toISOString(),
        version: '1.0.0'
      },
      filename: `portfolio-backup-${new Date().toISOString().split('T')[0]}.json`
    };
  }
}

// Export singleton instance
export const portfolioAuthIntegration = new PortfolioAuthIntegration();
